import APIService from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import LeafletMainMap from "@/components/core/LeafletMainMap"
import { AgentMapPoint } from "@/types/Agents.types"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Card, Form, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"

interface Props {
    agentId: string
    /** Fallback pin colour when the place has no status colour */
    agentColor?: string
}

type MapFilter = "all" | "assigned" | "portfolio"

const DEFAULT_CENTER = { lat: 18.479777, lng: -69.9341112 }

const AgentPortfolioMap = ({ agentId, agentColor }: Props) => {
    const { t } = useTranslation()
    const history = useHistory()
    const user = parsedUser()

    const [records, setRecords] = useState<AgentMapPoint[]>([])
    const [loading, setLoading] = useState(false)
    const [filter, setFilter] = useState<MapFilter>("all")

    const bindData = useCallback(async () => {
        if (!user?.OrganizationId || !agentId) return

        try {
            setLoading(true)
            const request = await APIService.getAction(
                "agents/PortfolioMap",
                `OrganizationId=${user.OrganizationId}&Id=${agentId}`
            )
            if (request.data.status === "ok") {
                setRecords(request.data.response || [])
            }
        } catch (ex) {
            console.error(ex)
        } finally {
            setLoading(false)
        }
    }, [user?.OrganizationId, agentId])

    useEffect(() => {
        bindData()
    }, [bindData])

    const filtered = useMemo(() => {
        if (filter === "assigned") return records.filter(x => x.assignedToAgent)
        if (filter === "portfolio") return records.filter(x => !x.assignedToAgent)
        return records
    }, [records, filter])

    // LeafletMainMap expects the Place shape used by the main visits map
    const mapRecords = useMemo(
        () =>
            filtered
                .filter(x => x.latitude && x.longitude)
                .map(x => ({
                    id: x.id,
                    name: x.name || x.creditCode || "",
                    latitude: Number(x.latitude),
                    longitude: Number(x.longitude),
                    created: x.created,
                    assignedTo: x.assignedToAgent ? agentId : undefined,
                    assignedToObj: x.assignedToAgent
                        ? { firstName: x.name || "A", lastName: "", color: agentColor }
                        : undefined,
                    placeStatus: x.statusName
                        ? { name: x.statusName, color: x.statusColor || "#6c757d" }
                        : undefined,
                    creditCode: x.creditCode,
                })),
        [filtered, agentId, agentColor]
    )

    const center = useMemo(() => {
        if (mapRecords.length === 0) return DEFAULT_CENTER
        const sum = mapRecords.reduce(
            (acc, r) => ({ lat: acc.lat + r.latitude, lng: acc.lng + r.longitude }),
            { lat: 0, lng: 0 }
        )
        return { lat: sum.lat / mapRecords.length, lng: sum.lng / mapRecords.length }
    }, [mapRecords])

    const assignedCount = records.filter(x => x.assignedToAgent).length

    if (!loading && records.length === 0) return null

    return (
        <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h6 className="mb-0">
                    <i className="fa fa-map-marked-alt me-2 text-primary" />
                    {t("Customers Map")}
                    <small className="text-muted ms-2">
                        {records.length} {t("located")}
                    </small>
                </h6>
                <Form.Select
                    size="sm"
                    style={{ width: "auto" }}
                    value={filter}
                    onChange={e => setFilter(e.target.value as MapFilter)}
                >
                    <option value="all">{t("All")} ({records.length})</option>
                    <option value="assigned">{t("Assigned Places")} ({assignedCount})</option>
                    <option value="portfolio">{t("Portfolio only")} ({records.length - assignedCount})</option>
                </Form.Select>
            </Card.Header>
            <Card.Body className="p-0" style={{ position: "relative" }}>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" size="sm" />
                    </div>
                ) : mapRecords.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        {t("No located customers for this agent")}
                    </div>
                ) : (
                    <LeafletMainMap
                        records={mapRecords}
                        center={center}
                        zoom={10}
                        totalRecordsWithCoords={mapRecords.length}
                        onMarkerClick={record => history.push(`/places/${record.id}`)}
                        style={{ width: "100%", height: "480px" }}
                    />
                )}
            </Card.Body>
        </Card>
    )
}

export default AgentPortfolioMap
